import { useCallback, useEffect, useState } from "react";

/**
 * The height an element can take to reach the bottom of its scrolling
 * ancestor: the ancestor's visible height, minus where the element starts
 * inside it, minus the gap wanted under it. Same idea as
 * useDynamicLayoutHeight (100dvh minus the top offset) but relative to the
 * nearest scroll container, so it works inside the sidebar layout's content
 * section — and it measures the element itself, so whatever sits above it
 * (a summary line that wraps, a bar of zone cards) is already accounted for.
 *
 * Re-measures when the scroll container resizes, when anything inside it
 * changes (async content above the element landing late), and on window
 * resize; a no-op when the number is unchanged, so setting the element's own
 * height from the result does not loop.
 *
 *   bottomPad  px between the element's bottom and the scroll area's bottom
 *              edge — the same figure as the content's bottom padding leaves
 *              the page exactly filled, with nothing to scroll.
 *   min        px floor; below it the page scrolls rather than crush the chart.
 *
 * Returns { ref, height } — `ref` is a callback ref, so the element may
 * mount later than the hook's first run (after a skeleton, say); height is
 * null until the first measurement.
 */
const scrollParentOf = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

export const useFillScrollParentHeight = ({ bottomPad = 20, min = 360 } = {}) => {
  const [el, setEl] = useState(null);
  const ref = useCallback((node) => setEl(node), []);
  const [height, setHeight] = useState(null);

  useEffect(() => {
    const sp = scrollParentOf(el);
    if (!el || !sp) return;

    let last = null;
    const measure = () => {
      const top = el.getBoundingClientRect().top - sp.getBoundingClientRect().top + sp.scrollTop;
      const next = Math.max(min, Math.floor(sp.clientHeight - top - bottomPad));
      if (next === last) return;
      last = next;
      setHeight(next);
    };

    let rafId = null;
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        measure();
      });
    };

    measure();
    const ro = new ResizeObserver(schedule);
    ro.observe(sp);
    const mo = new MutationObserver(schedule);
    mo.observe(sp, { childList: true, subtree: true, characterData: true, attributes: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [el, bottomPad, min]);

  return { ref, height };
};
